'use client';
import React, { FC, Suspense, useContext, useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import { LineItem } from 'shared/types/cart';
import { ProjectSettings } from 'shared/types/projectSettings/ProjectSettings';
import { TealiumUTag } from 'shared/types/tealium/TealiumUTag';
import { getCookie } from 'typescript-cookie';
import { getCookieZipCode } from '@/components/customercenter-ui/utils/cookieUtils';
import {
  addZipToCookie,
  createFedexRequestPayload,
  getArrivalDateFromResponse,
  getDefaultDeliveryDate,
  getDeliverydateFromStorage,
} from '@/components/customercenter-ui/utils/fedexUtils';
import { triggerTealiumEvent, getPageUtagData } from '@/components/customercenter-ui/utils/tealiumUtils';
import { CreditSummaryContext } from '@/context/creditSummaryProvider';
import WarningIcon from '@/customercenter-ui/svgs/notifications/warning';
import { useCart, useProduct } from '@/frontastic';
import { useProjSettings } from '@/frontastic/hooks/useProjectSettings/useProjectSettings';
import { CurrencyHelpers } from '@/helpers/utils/currencyHelpers';
import { mapLocaleToMeaningfulFormat } from '@/helpers/utils/i18n';
import { getPageUrl, PathNames, TranslateText, getDivisionName, getSelectedDivision } from '@/helpers/utils/siteUtils';
import { GetEstimatedDeliveryDatePayload } from '@/sdk/composable-commerce/types/payloads/GetEstimatedDeliveryDatePayload';
import OrderAdjustments from './components/Adjustments';
import CartItem from './components/CartItem';
import Notes from './components/Notes';
import CustomerOrderSummary from './components/OrderSummary';
import UpsellItems from './components/UpsellItems';
import { PageTexts } from './i18n';
import { sortNotesByTimestamp } from './utils';
import { DEFAULT_SITE_LANG, ORDER_TYPES, COOKIE_KEYS } from '../../../../constants/constant';
import { getAvailableCredit } from '../customer-detail/utils';
import { getContentByTitle } from '../product/details/helpers/utils/productDataUtils';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import BackButton from '../ui/back-button';
import Loader from '../ui/loader';
import { getCurrentCentralTime, maskPostalCode } from '../utils/formatUtils';

const CreditSummary = dynamic(() => import('../customer-detail/components/CreditSummary/creditSummary'), {
  ssr: false,
  loading: () => null,
});
export interface CartProps {
  widgetData: any;
}

const Cart: FC<any> = (widgetData) => {
  const { data, revalidateCart, beginNoChargeOrder } = useCart(false);
  const { customerData } = useContext(CreditSummaryContext);
  const Texts = TranslateText(PageTexts, DEFAULT_SITE_LANG);
  const notes = data?.additionalDetails?.notes?.notes ?? [];
  const reasons = data?.additionalDetails?.notes?.reasons ?? [];
  const sortedNotes = sortNotesByTimestamp([...notes, ...reasons]);
  const [loading, setLoading] = useState(false);
  const [backUrl, setBackUrl] = useState('');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isDataLoading, setIsDataLoading] = useState(true);
  const projectSettings: ProjectSettings = useProjSettings();
  const [siteId, setSiteId] = useState<string>();
  const allowedCreditSites = JSON.parse(projectSettings?.allowedCreditSites ?? 'ZBSite');
  const [excludedBrands, setExcludedBrands] = useState();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [notificationMessage, setNotificationMessage] = useState<string>('');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isError, setIsError] = useState<boolean>(false);
  const availableCredit = getAvailableCredit(customerData);
  const orderTotal = Number(CurrencyHelpers.customCurrencyFormat(data?.total || {})?.replace(/[$,]/g, ''));
  const isOrderTotalOverAvailableCredit = availableCredit ? orderTotal > availableCredit : false;

  useEffect(() => {
    const backurl = getPageUrl(PathNames.SEARCH);
    const basePath = backurl || '/search/';
    const orderDetailsUrl = getPageUrl(PathNames.ORDER_DETAILS);
    const url = new URL(`${window.location.origin}${basePath}`);
    url.searchParams.set('backUrl', 'true');
    if (!backurl && orderDetailsUrl) {
      url.searchParams.set('fromOrder', 'true');
    }
    setBackUrl(url.toString());
    const reVaidateCart = async () => {
      setLoading(true);
      await revalidateCart();

      if (data?.additionalDetails?.orderType === ORDER_TYPES.NO_CHARGE) {
        await beginNoChargeOrder();
      }
      setLoading(false);
    };
    reVaidateCart();
    if (!excludedBrands) {
      try {
        getContentByTitle(widgetData?.widgetData?.excludedBrands).then((data: any) => {
          setExcludedBrands(data?.aceEditor_Html_CssEntries?.[0]?.html_content);
        });
      } catch (e) {
        console.error('Error while fetching data exculded brands');
      }
    }
  }, []);

  useEffect(() => {
    const cookieSiteId = getCookie(COOKIE_KEYS.selectedSite) ?? 'ZBSite';
    setSiteId(cookieSiteId);
  }, []);

  useEffect(() => {
    if (data?.lineItems) {
      setIsDataLoading(false);
    }
  }, [data]);

  useEffect(() => {
    if (data?.lineItems) {
      const pageUtagData: TealiumUTag = getPageUtagData(
        'Basket',
        undefined,
        undefined,
        undefined,
        data,
        undefined,
        undefined,
        projectSettings,
      );
      triggerTealiumEvent(pageUtagData);
    }
  }, [data]);
  const { getEstimatedDeliveryDate } = useProduct();
  const [initialArrivalDate, setInitialArrivalDate] = useState('');

  const getArrivalDate = async (zipCode: string, pageName?: string): Promise<string> => {
    let arrivalDateValue = '';
    if (zipCode) {
      const payload: GetEstimatedDeliveryDatePayload = createFedexRequestPayload(zipCode, pageName);
      const response: any = await getEstimatedDeliveryDate(payload);
      if (
        (!response?.isError || response?.data?.isSuccess) &&
        response?.data?.fedexResponseDetails?.smartPostDeliveryDate
      ) {
        arrivalDateValue = response?.data?.fedexResponseDetails?.smartPostDeliveryDate;
        addZipToCookie(zipCode);
      } else {
        addZipToCookie(zipCode);
        throw Error('Error while fetching delivery date');
      }
    }
    return arrivalDateValue || '';
  };

  const fetchArrivalDate = async () => {
    try {
      const zipCodeonLoad = getCookieZipCode();
      const deliveryDateDataLocalStorage = getDeliverydateFromStorage(zipCodeonLoad, 'deliveryDateData');
      let deliveryDate;
      if (!deliveryDateDataLocalStorage) {
        deliveryDate = await getArrivalDate(zipCodeonLoad, 'CART');
        const deliveryDateData = {
          zipCode: zipCodeonLoad,
          deliveryDate: deliveryDate,
          isSucess: true,
          date: getCurrentCentralTime(),
        };
        localStorage.setItem('deliveryDateData', JSON.stringify(deliveryDateData));
      } else {
        deliveryDate = deliveryDateDataLocalStorage;
      }
      if (deliveryDate) {
        const arrivalDateValue = getArrivalDateFromResponse(deliveryDate, false, false, projectSettings);
        setInitialArrivalDate(arrivalDateValue);
      }
    } catch (error) {
      const defaultArrivalDate = getDefaultDeliveryDate(
        mapLocaleToMeaningfulFormat().standardShipMethod,
        projectSettings?.defaultDeliveryDates,
        false,
        false,
        projectSettings,
      );
      setInitialArrivalDate(defaultArrivalDate);
      addZipToCookie(maskPostalCode(getCookieZipCode()));
    }
  };

  useEffect(() => {
    if (!projectSettings?.isFedexEnabled) {
      const defaultArrivalDate = getDefaultDeliveryDate(
        mapLocaleToMeaningfulFormat().standardShipMethod,
        projectSettings?.defaultDeliveryDates,
        false,
        false,
        projectSettings,
      );
      setInitialArrivalDate(defaultArrivalDate);
    } else {
      fetchArrivalDate();
    }
  }, []);

  return (
    <section className="px-6 py-5">
      {loading && <Loader />}
      {/* chng-text-22px */}
      <div className="flex items-center justify-between">
        <p className="pb-5 text-xl font-bold">
          {data?.lineItems?.reduce((currentQuantity: number, item: any) => {
            // dont include warranty items to total count
            if (!item.lineGroupId && !item.isInsuranceItem) {
              return currentQuantity + (item.count || 0);
            }
            return currentQuantity;
          }, 0)}
          <span className="ml-1">{Texts.itemsInCart} </span>
        </p>
        <BackButton text={Texts.backToCatalog} className={`flex text-blueLight underline`} url={backUrl} />
      </div>
      <section className="flex flex-wrap gap-4 lg:flex-nowrap">
        <section className="w-[75%]">
          {isOrderTotalOverAvailableCredit && (
            <div className="mb-5">
              <Accordion type="single" collapsible>
                <AccordionItem value="item-1" className="bg-[#FFF4E6] text-black">
                  <AccordionTrigger className="flex w-full items-start gap-3 p-3 hover:no-underline focus:outline-none">
                    <span className="shrink-0 transform-none">
                      <WarningIcon />
                    </span>
                    <p className="w-full text-left text-sm font-medium text-black">
                      {
                        mapLocaleToMeaningfulFormat([(orderTotal - availableCredit)?.toFixed(2)])
                          .orderOverAvailableCredit
                      }
                    </p>
                  </AccordionTrigger>
                  <AccordionContent className="pb-2">
                    <div className="mx-3 border-t py-1 pt-2 text-xs">
                      <p className="mb-1 font-semibold text-yellow-900">
                        {mapLocaleToMeaningfulFormat()?.availableCreditOverageText}
                      </p>
                      <p className="font-medium text-black">
                        {
                          mapLocaleToMeaningfulFormat([getDivisionName(getSelectedDivision() ?? 'ZB')])
                            .availableCreditOverage
                        }
                      </p>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          )}
          <div className="flex flex-col gap-4">
            <div className=" rounded-md border-foggyGray shadow-lg">
              {data?.lineItems
                ?.slice()
                .reverse()
                .map((lineItem: LineItem) => {
                  const selectedItem = data.lineItems?.find((item) => item.lineGroupId === lineItem.lineItemId);
                  if (!lineItem.lineGroupId && !lineItem.isInsuranceItem) {
                    return (
                      <CartItem
                        cartItem={lineItem}
                        key={lineItem.lineItemId}
                        selectedWarranty={selectedItem ?? undefined}
                        setIsDataLoading={setIsDataLoading}
                        excludedBrands={excludedBrands}
                        initialArrivalDate={initialArrivalDate}
                        setInitialArrivalDate={setInitialArrivalDate}
                        setIsError={setIsError}
                        setNotificationMessage={setNotificationMessage}
                      />
                    );
                  }
                })}
            </div>
            <UpsellItems widgetData={widgetData}></UpsellItems>
            <Notes Texts={Texts} notesTableData={sortedNotes}></Notes>
            <OrderAdjustments
              Texts={Texts}
              pageName="cart"
              totalPrice={data?.subTotal}
              noteNumber={[...notes, ...reasons]?.length}
            />
            {/* <PriorPromos Texts={Texts} /> */}
          </div>
        </section>
        <section className="w-[25%]">
          <div className="flex flex-col gap-4">
            <CustomerOrderSummary Texts={Texts} widgetData={widgetData} pageName="cart" />
            <Suspense
              fallback={
                <div>
                  <Loader />
                </div>
              }
            >
              {allowedCreditSites?.creditSites?.includes(siteId) && <CreditSummary />}
            </Suspense>
          </div>
        </section>
      </section>
    </section>
  );
};

export default Cart;
